import { Store } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface LocationOption {
  accountId: string;
  accountName: string;
  locationId: string;
  locationName: string;
  address?: string | null;
}

interface LocationPickerDialogProps {
  open: boolean;
  locations: LocationOption[];
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (accountId: string, locationId: string) => void;
}

export function LocationPickerDialog({
  open,
  locations,
  isSubmitting,
  onCancel,
  onSubmit,
}: LocationPickerDialogProps) {
  const [selected, setSelected] = useState<{ accountId: string; locationId: string } | null>(null);

  const handleSubmit = () => {
    if (!selected) return;
    onSubmit(selected.accountId, selected.locationId);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSubmitting) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose your business location</DialogTitle>
          <DialogDescription>
            Your Google account manages multiple locations. Pick the one for this salon.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {locations.map((location) => {
            const isSelected =
              selected?.accountId === location.accountId &&
              selected?.locationId === location.locationId;
            return (
              <button
                key={`${location.accountId}::${location.locationId}`}
                type="button"
                onClick={() =>
                  setSelected({ accountId: location.accountId, locationId: location.locationId })
                }
                className={cn(
                  "w-full flex items-start gap-3 p-3 rounded-md border text-left transition-colors",
                  isSelected
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950"
                    : "border-border hover:bg-accent"
                )}
              >
                <Store className="w-5 h-5 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{location.locationName}</div>
                  {location.address ? (
                    <div className="text-sm text-muted-foreground truncate">{location.address}</div>
                  ) : null}
                  <div className="text-xs text-muted-foreground mt-1">
                    Account: {location.accountName}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selected || isSubmitting}>
            {isSubmitting ? "Connecting..." : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
